import React, { memo, useEffect, useState } from 'react'
import { useHistory, useParams } from 'react-router-dom'
import { Box, createStyles, makeStyles, Typography } from 'transfix-ui/core'
import { ShipperRateObject, useGetShipperRate } from 'apps/marketplace/apiTypes/shipperRates'
import { Dialog } from 'components/layout'
import { LaneArrowIcon } from 'transfix-icons'
import { brandColors, Spinner, theme } from 'transfix-ui'
import { formatCityState } from 'utils/formatCityState'
import { ShipperRateHistoryTable } from './ShipperRateHistoryTable'

interface IShipperRateHistoryProps {
  setSidebarOpen: (value: boolean) => void
  sidebarOpen: boolean
}

const useShipperRateHistoryStyles = makeStyles(() =>
  createStyles({
    titleText: {
      display: 'flex',
      flex: theme.spacing(1),
      alignItems: 'center',
    },
    secondary: {
      color: brandColors.coolGray5,
      marginLeft: theme.spacing(2),
      marginRight: theme.spacing(2),
    },
  })
)

export const ShipperRateHistory = memo<IShipperRateHistoryProps>(
  ({ setSidebarOpen, sidebarOpen }) => {
    const [, setCurrentRate] = useState<ShipperRateObject | undefined>()

    const classes = useShipperRateHistoryStyles()

    const { id: shipperRateId } = useParams<{ id: string }>()
    const history = useHistory()

    const { data: shipperRateData, loading, refetch: getShipperRate } = useGetShipperRate({
      shipperRateId: Number(shipperRateId),
      queryParams: {
        includes: JSON.stringify(['repriced_rates']),
      },
      lazy: true,
    })

    useEffect(() => {
      if (shipperRateData) {
        setCurrentRate(shipperRateData)
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [shipperRateData])

    useEffect(() => {
      const loadRate = async () => {
        await getShipperRate()
      }
      if (shipperRateId) {
        loadRate()
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [shipperRateId])

    const closeDialogHandler = () => {
      setSidebarOpen(false)
      history.replace('')
    }

    return (
      <>
        <Dialog
          id='shipper-rate-history-side-panel'
          size='large'
          align='right'
          closeDialog={closeDialogHandler}
          dialogOpen={sidebarOpen}>
          {!loading ? (
            <Box mb={3}>
              <Box display='flex' justifyContent='center' alignItems='center' pl={2} pr={2}>
                <Typography variant='h2' className={classes.titleText}>
                  <span data-test='shipper-rate-history-id-header'>Rate History</span>
                  <span className={classes.secondary}>|</span>
                  <span data-test='shipper-rate-history-pickup-header'>
                    {formatCityState(
                      shipperRateData?.pickup?.city || undefined,
                      shipperRateData?.pickup?.stateCode || undefined
                    )}
                  </span>
                  <LaneArrowIcon size='xlarge' className={classes.secondary} />
                  <span className='mr2' data-test='shipper-rate-history-delivery-header'>
                    {formatCityState(
                      shipperRateData?.delivery?.city || undefined,
                      shipperRateData?.delivery?.stateCode || undefined
                    )}
                  </span>
                  ({shipperRateData?.awardType})
                </Typography>
              </Box>
            </Box>
          ) : (
            <Spinner />
          )}
          <hr />
          {shipperRateData && (
            <ShipperRateHistoryTable shipperRate={shipperRateData} loading={loading} />
          )}
        </Dialog>
      </>
    )
  }
)
